import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { splitFavorites } from '@radevents/shared';
import { newsService } from '../services/newsService';
import communityService from '../services/communityService';

/**
 * Kleine Hinweise an der Navigation (Kopf am Rechner, Tab-Leiste am Handy), 28.10.2026.
 *
 * Beide fragen den Server und speichern **nichts** im Browser: ein „zuletzt gelesen“ im
 * localStorage wäre keine vom Nutzer gewählte Einstellung und damit ein Fall für den
 * Cookie-Hinweis (§ 25 TDDDG). Der Kopf steht auf jeder Seite neu, deshalb hält ein Modul-Speicher
 * die Antwort ein paar Minuten, statt bei jedem Seitenwechsel neu zu fragen.
 */

/** Ein Artikel gilt so lange als neu. Für alle gleich, ob angemeldet oder nicht. */
export const NEWS_NEU_TAGE = 3;
const NEWS_HALTEN_MS = 10 * 60 * 1000;
const ZAHL_HALTEN_MS = 60 * 1000;

let newsStand: { at: number; neu: boolean; laeuft?: Promise<boolean> } | null = null;

function ladeNewsNeu(): Promise<boolean> {
  if (newsStand?.laeuft) return newsStand.laeuft;
  if (newsStand && Date.now() - newsStand.at < NEWS_HALTEN_MS) return Promise.resolve(newsStand.neu);
  // Über `then` gestartet: auch ein Fehler beim Aufruf selbst endet als „kein Punkt“, nie als Absturz des Kopfs.
  const laeuft = Promise.resolve().then(() => newsService.getAll(0, 1))
    .then(page => {
      const datum = page.content[0]?.publishedAt;
      const neu = !!datum && Date.now() - new Date(datum).getTime() < NEWS_NEU_TAGE * 86400000;
      newsStand = { at: Date.now(), neu };
      return neu;
    })
    .catch(() => { newsStand = { at: Date.now(), neu: false }; return false; });
  newsStand = { at: 0, neu: newsStand?.neu ?? false, laeuft };
  return laeuft;
}

/**
 * Wer News geöffnet hat, sieht den Punkt nicht mehr. Gemerkt wird das nur im Arbeitsspeicher der
 * Seite, nicht im Browser (siehe oben): nach dem Neuladen oder beim nächsten Besuch steht der Punkt
 * wieder da, solange der Artikel jünger als drei Tage ist.
 */
let newsGesehen = false;

/** Punkt an „News“: der neueste Artikel ist höchstens drei Tage alt und News noch nicht geöffnet. */
export function useNeueNews(): boolean {
  const { pathname } = useLocation();
  if (pathname.startsWith('/news')) newsGesehen = true;
  const [neu, setNeu] = useState(() => newsStand?.neu ?? false);
  useEffect(() => {
    let aktiv = true;
    ladeNewsNeu().then(n => { if (aktiv) setNeu(n); });
    return () => { aktiv = false; };
  }, []);
  return neu && !newsGesehen;
}

let zahlStand: { at: number; zahl: number } | null = null;

const VERALTET = 'radevents:meine-events';

/** Wird gemerkt oder nicht mehr: die Zahl im Kopf sofort neu holen, auch ohne Seitenwechsel. */
export function meineEventsVeraltet() {
  zahlStand = null;
  window.dispatchEvent(new Event(VERALTET));
}

/** Zahl der kommenden eigenen Events (dieselbe Liste wie „Meine Events“), `null` ohne Konto. */
export function useMeineEventsZahl(angemeldet: boolean): number | null {
  const [zahl, setZahl] = useState<number | null>(() => (angemeldet ? zahlStand?.zahl ?? null : null));
  const [runde, setRunde] = useState(0);
  useEffect(() => {
    const neu = () => setRunde(r => r + 1);
    window.addEventListener(VERALTET, neu);
    return () => window.removeEventListener(VERALTET, neu);
  }, []);
  useEffect(() => {
    if (!angemeldet) { setZahl(null); return; }
    if (zahlStand && Date.now() - zahlStand.at < ZAHL_HALTEN_MS) { setZahl(zahlStand.zahl); return; }
    let aktiv = true;
    Promise.resolve().then(() => communityService.planning('mine'))
      .then(list => {
        const n = splitFavorites(list).upcoming.length;
        zahlStand = { at: Date.now(), zahl: n };
        if (aktiv) setZahl(n);
      })
      .catch(() => {});
    return () => { aktiv = false; };
  }, [angemeldet, runde]);
  return zahl;
}
